import { Body, Heading, Icon } from "@elaniintc/goes-design-system";
import { useEffect, useState, useMemo } from "react";
import TablePaginantion from "@/components/common/table/pagination";
import { KEY_NODES_DEF } from "./node-list-filters";
import InsolvencyWarningBadge from "../form/insolvency-warning-badge";
import { FinishStatusBanner } from "../form/finish-status-banner";
import { ResolutionNodeCard } from "../resolution/ResolutionCard";
import { ActionNodeList } from "./action-node-list";
import { SystemNodeList } from "./system-node-list";
import type { NodeExecutionType } from "@/types/entities/flows";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import { useGetInsolvencies } from "@/queries/users/insolvencies.api";
import { useInfo } from "../../hooks/use-info";

const ITEMS_PER_PAGE = 20;

interface NodeListProps {
  procedureId?: string;
  isExpired?: boolean;
  uniqueExecutionNodes: Array<NodeExecutionType>;
  isExecutionLoading: boolean;
  formalityRequest?: FormalityRequestType;
}

export const NodeList = ({
  isExpired,
  procedureId,
  uniqueExecutionNodes,
  isExecutionLoading,
  formalityRequest,
}: NodeListProps) => {
  const { isCancelled, isCompleted } = useInfo();
  const [currentPage, setCurrentPage] = useState(1);

  const expedientsId = useMemo(() => {
    return (
      formalityRequest?.associated_expedient_registries
        ?.map((expedient) => expedient.id)
        .concat(formalityRequest?.applicant_id ?? "") || []
    );
  }, [formalityRequest]);

  const { data: insolvencies } = useGetInsolvencies(
    formalityRequest?.applicant_id ?? "",
    expedientsId,
  );

  const hasActiveInsolvencies = insolvencies?.data?.some(
    (insolvency) => insolvency.status === "ACTIVE",
  );
  // Paginación
  const totalItems = uniqueExecutionNodes.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const keyNextNodes = useMemo(() => {
    // validate if the cancellation request is approved, if so, return an empty array
    if (isCancelled) {
      return [];
    }
    return uniqueExecutionNodes?.filter((node) => {
      if (!node?.node) return false;
      const keyNodeType = Array.from(KEY_NODES_DEF).find(
        (keyNode) => keyNode.name === node.node.ref_name,
      );
      if (!keyNodeType) return false;
      // verifica el estado del nodo en el primer nivel de filtrado
      const firstLevelStatus = keyNodeType.states.includes(node.status);
      if (!firstLevelStatus) return false;
      // si existe un filtro adicional, aplicarlo
      // solo se aplica si el nodo pasa el primer nivel de filtrado
      if (keyNodeType.filter) {
        return keyNodeType.filter(node);
      }
      return firstLevelStatus;
    });
  }, [isCancelled, uniqueExecutionNodes]);

  const nonKeyNextNodes = useMemo(() => {
    const keyNodeIds = new Set(
      keyNextNodes.map((node) => `${node.id}:${node.flow_execution_id}`),
    );

    return uniqueExecutionNodes.filter(
      (node) => !keyNodeIds.has(`${node.id}:${node.flow_execution_id}`),
    );
  }, [keyNextNodes, uniqueExecutionNodes]);

  const paginatedNodes = useMemo(
    () => nonKeyNextNodes.slice(startIndex, endIndex),
    [nonKeyNextNodes, startIndex, endIndex],
  );

  // Reset to page 1 when nodes change
  useEffect(() => {
    setCurrentPage(1);
  }, [totalItems]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <ActionNodeList
          procedureId={procedureId as string}
          formalityRequest={formalityRequest}
          nodes={keyNextNodes}
          isLoading={isExecutionLoading}
          showNodeSkeleton={uniqueExecutionNodes.length === 0}
          hidden={isCancelled || isCompleted}
        />

        {(isCancelled || hasActiveInsolvencies) && (
          <div>
            <Heading variant="h6" className="text-heading">
              Pasos de su solicitud
            </Heading>
            <Body className="text-body-quiet text-sm mt-1">
              Revise los pasos de la solicitud actual.
            </Body>
          </div>
        )}

        <InsolvencyWarningBadge
          userId={formalityRequest?.applicant_id ?? ""}
          userName={formalityRequest?.name ?? ""}
          expedientsId={
            formalityRequest?.associated_expedient_registries
              ?.map((expedient) => expedient.id)
              .concat(formalityRequest?.applicant_id ?? "") || []
          }
        />
      </div>

      <FinishStatusBanner
        formalityRequest={formalityRequest}
        formalityCode={formalityRequest?.code || ""}
        requestGroups={formalityRequest?.groups}
        isCancelled={isCancelled}
        isCompleted={isCompleted}
        hidden={uniqueExecutionNodes.length === 0 && isExecutionLoading}
      />
      <div className="flex flex-col gap-3">
        {/* Nuevo listado de nodos – usando paginatedNodes */}
        <SystemNodeList
          procedureId={procedureId as string}
          formalityRequest={formalityRequest}
          startIndex={startIndex}
          nodes={paginatedNodes}
          showNodeSkeleton={uniqueExecutionNodes.length === 0}
          showLoading={uniqueExecutionNodes.length === 0}
          isLoading={
            (isExecutionLoading && nonKeyNextNodes.length > 0) ||
            uniqueExecutionNodes.length === 0 // used to show general loading message
          }
          hideHeader={isCancelled || hasActiveInsolvencies}
        />

        {/* Render resolution card if the formality request has a resolution and it wasn't rendered in the list */}
        {formalityRequest?.resolution &&
          !uniqueExecutionNodes.some((node) => {
            const rawType = (node.node.type || "").toUpperCase();
            const refName = (node.node.ref_name || "").toLowerCase();
            return rawType === "RESOLUTION" || refName === "resolution-node";
          }) && (
            <ResolutionNodeCard
              procedureId={procedureId as string}
              index={uniqueExecutionNodes.length}
              tramite={formalityRequest}
            />
          )}

        {/* Maquetado del objeto tiempo caducado */}
        {isExpired && (
          <div className="bg-[#FF002C33] border-[0.5px] border-[#FF002C] p-3 rounded-xl">
            <section style={{ display: "flex", gap: 6 }}>
              <div className="flex items-center justify-center border-[0.5px] border-[#FF002C] size-6 bg-[#FF002C33] rounded-full">
                <Icon
                  size="xs"
                  type="fas"
                  name="clock"
                  className="text-danger-quiet"
                />
              </div>
              <h2 className="text-md font-semibold text-[#B91C1C]">
                Tu tiempo ha caducado para un paso de esta solicitud
              </h2>
            </section>
            <section className="text-sm text-[#EF4444]">
              Revisa la solicitud y atiende el paso lo antes posible.
            </section>
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="mt-4">
            <TablePaginantion
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              onNext={handleNext}
              onPrevious={handlePrevious}
              hasNext={currentPage < totalPages}
              hasPrevious={currentPage > 1}
            />
          </div>
        )}
      </div>
    </div>
  );
};
